/* The map.
 *
 * IDENTICAL on both surfaces — Addendum A2: "a geologist must recognise the
 * desk map as 'the same map' as their phone." That is not a nicety. Someone is
 * describing a location over the radio to someone looking at the other screen.
 *
 * ── What this is made of, and why ────────────────────────────────────────
 *
 * A default vector basemap is a road map: it is drawn to help you drive
 * somewhere. A geologist walking claims needs almost none of that and needs
 * two things it doesn't have — SHAPE OF THE GROUND and ELEVATION.
 *
 * So the basemap is built bottom-up for this job:
 *
 *   1. HILLSHADE from a real DEM (AWS terrarium tiles, keyless). The terrain
 *      is the primary layer, not decoration — the shape of a ridge is what you
 *      navigate by out there.
 *   2. CONTOURS computed ON DEVICE from the same DEM by maplibre-contour, in a
 *      worker. No contour tile service exists for free, and this is better
 *      anyway: the interval adapts to zoom, index contours carry labels, and
 *      once the DEM tiles are cached it keeps working with no signal.
 *   3. Roads and water from OpenFreeMap (keyless), turned right down. You need
 *      the track you drove in on and the wash you're standing in. Nothing else.
 *
 * Everything is coloured from the Kilroy ramps. No stock basemap palette
 * survives contact with this file, because a map that looks like Google Maps
 * with dots on it is a map nobody believes is theirs.
 *
 * No API keys anywhere. Fully self-hostable later: swap two URLs for the
 * property pmtiles packs and this whole style works offline unchanged.
 */

import { brand, neutral, warn, win } from './tokens';

/* ── Sources ─────────────────────────────────────────────────────────────── */

/** Free, keyless, global elevation. terrarium encoding. */
export const DEM_TILES =
  'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';

/** Free, keyless vector basemap (OpenMapTiles schema). */
export const VECTOR_TILES = 'https://tiles.openfreemap.org/planet';
export const GLYPHS = 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf';

/** Set by the app after it registers the maplibre-contour protocol. */
export const CONTOUR_PROTOCOL = 'kilroyDem';

export const SOURCES = {
  dem: 'kilroy-dem',
  contours: 'kilroy-contours',
  base: 'kilroy-base',
  evidence: 'kilroy-evidence',
  assignments: 'kilroy-assignments',
  claims: 'kilroy-claims',
} as const;

/* ── Published hydrothermal alteration (USGS) ────────────────────────────────
 *
 * USGS OFR 2013-1139, served as WMS. Verified live: GetCapabilities 200, CORS
 * `access-control-allow-origin: *`, EPSG:3857 advertised and rendering.
 *
 * Note the parent `Hydrothermal_alteration` is a *group*, not a drawable layer
 * — requesting it returns LayerNotDefined. The five children below are the real
 * layers, and each is a different claim about the ground, so each gets its own
 * toggle rather than being bundled into one "alteration" switch.
 *
 * Every field here drives something: the WMS request, the style layer, the
 * toggle, and the legend swatch. One table so they cannot drift apart.
 */
export const ALTERATION_WMS = 'https://mrdata.usgs.gov/services/alteration';

export interface AlterationDef {
  /** WMS layer name. Also the style layer id, prefixed. */
  wms: string;
  label: string;
  /** What the mineral assemblage implies, in a geologist's terms. */
  meaning: string;
  /** The colour USGS actually draws, sampled from a live tile. */
  swatch: string;
  /** Share of the Blue Bell / DeSoto bounding box this layer covers, measured
      from a 512×512 GetMap over 34.25,-112.65 → 34.65,-112.15. */
  coveragePct: number;
}

export const ALTERATION: AlterationDef[] = [
  {
    wms: 'hydro_silica',
    label: 'Silicification',
    meaning: 'Hydrothermal silica — chert, jasper, silicified rock',
    swatch: '#A0A0A0',
    coveragePct: 2.88,
  },
  {
    wms: 'phyllic',
    label: 'Phyllic',
    meaning: 'Sericite · muscovite · illite — the 2.20 µm Al-OH feature',
    swatch: '#FFFF00',
    coveragePct: 1.91,
  },
  {
    wms: 'argillic',
    label: 'Argillic',
    meaning: 'Kaolinite · alunite · pyrophyllite — the 2.17 µm feature',
    swatch: '#FD0000',
    coveragePct: 1.31,
  },
  {
    wms: 'epi_chlor',
    label: 'Propylitic',
    meaning: 'Epidote · chlorite — the 2.33 µm Mg-OH feature',
    swatch: '#00FD00',
    coveragePct: 0.39,
  },
  {
    wms: 'carbonate',
    label: 'Carbonate',
    meaning: 'Calcite · dolomite',
    swatch: '#0000FD',
    coveragePct: 0.07,
  },
];

export const alterationLayerId = (wms: string) => `alteration-${wms}`;
export const alterationSourceId = (wms: string) => `kilroy-alt-${wms}`;

/** The WMS GetMap template MapLibre fills in per tile. */
export const alterationTileUrl = (wms: string): string =>
  `${ALTERATION_WMS}?service=WMS&version=1.3.0&request=GetMap` +
  `&layers=${wms}&crs=EPSG:3857&bbox={bbox-epsg-3857}` +
  `&width=256&height=256&format=image/png&transparent=true`;

/* Caveats that belong on the panel, not in a doc nobody opens.
 *
 * Every one of these is from USGS's own validation of the algorithm that made
 * these polygons (Mars 2014, SIR 2010-5090-O), not our scepticism about it. */
export const ALTERATION_NOTES = {
  source: 'USGS OFR 2013-1139 · published polygons, not computed here',
  coverage:
    'These cover about 6% of the property between them. A layer that draws almost nothing is not saying there is no alteration here — it is saying this survey mapped almost none.',
  facies:
    'These are alteration facies, not minerals. ASTER cannot separate kaolinite from alunite, so treat the label as a group.',
  commission:
    'The algorithm behind these over-maps by 40–55%. USGS checked the excess and most of it was alluvial fans of altered clasts — real altered rock that has moved downhill. Read a polygon as a candidate, and read a fan as a pointer to its source uphill.',
  colour: 'Colours are USGS’s own, kept so figures cross-reference against the published map.',
} as const;

/* ── Layers the user can toggle ──────────────────────────────────────────── */

export type LayerId =
  | 'hillshade'
  | 'contours'
  | 'claims'
  | 'assignments'
  | 'evidence'
  | 'position'
  /* One per published alteration facies. Template literal rather than five
     hand-written members, so adding a row to ALTERATION cannot leave the union
     behind — the failure mode that made a GPS failure render as "Settling…". */
  | `alt-${string}`;

export interface LayerDef {
  id: LayerId;
  label: string;
  defaultOn: boolean;
  /** Every style layer this toggle controls. */
  layers: string[];
  /** Panel grouping. Undefined means the base group. */
  group?: string;
  /** Legend swatch, where the layer draws in a fixed colour. */
  swatch?: string;
  /** One line under the label. */
  note?: string;
}

export const LAYERS: LayerDef[] = [
  { id: 'hillshade', label: 'Terrain', defaultOn: true, layers: ['hillshade'] },
  {
    id: 'contours',
    label: 'Contours',
    defaultOn: true,
    layers: ['contour-line', 'contour-index', 'contour-label'],
  },
  { id: 'claims', label: 'Claim boundaries', defaultOn: true, layers: ['claims-line'] },
  {
    id: 'assignments',
    label: 'Assignments',
    defaultOn: true,
    layers: ['assignments-fill', 'assignments-line'],
  },
  { id: 'evidence', label: 'Evidence', defaultOn: true, layers: ['evidence'] },
  { id: 'position', label: 'My position', defaultOn: true, layers: ['position-ring', 'position-dot'] },

  /* Published alteration. Off by default: it is a survey someone else did, and
     it should be something you reach for rather than something that greets you
     on a screen you opened to find your own pit. */
  ...ALTERATION.map(
    (a): LayerDef => ({
      id: `alt-${a.wms}`,
      label: a.label,
      defaultOn: false,
      layers: [alterationLayerId(a.wms)],
      group: 'Published alteration',
      swatch: a.swatch,
      note: a.meaning,
    }),
  ),
];

/** Panel groups, in order, derived from LAYERS so the two cannot disagree. */
export const LAYER_GROUPS: { group?: string; layers: LayerDef[] }[] = [
  { group: undefined, layers: LAYERS.filter((l) => !l.group) },
  ...[...new Set(LAYERS.map((l) => l.group).filter(Boolean))].map((g) => ({
    group: g as string,
    layers: LAYERS.filter((l) => l.group === g),
  })),
];

/* ── The style ───────────────────────────────────────────────────────────── */

export interface StyleOptions {
  /** When false the basemap is omitted and only terrain + overlays draw. */
  online?: boolean;
  /**
   * Contour tile URL, produced by maplibre-contour's DemSource in the app.
   * The app owns it because the worker that computes the lines has to be
   * registered against that app's maplibre instance. Omit it and the contour
   * layers simply don't render — the map degrades, it never errors.
   */
  contourUrl?: string;
  /** pmtiles:// URL of a downloaded property pack, if one exists. */
  packUrl?: string;
}

export function kilroyStyle(opts: StyleOptions = {}): Record<string, unknown> {
  const { online = true } = opts;

  const sources: Record<string, unknown> = {
    [SOURCES.dem]: {
      type: 'raster-dem',
      tiles: [DEM_TILES],
      encoding: 'terrarium',
      tileSize: 256,
      maxzoom: 13,
      attribution: 'Elevation: Mapzen / AWS Terrain Tiles',
    },
  };

  if (opts.contourUrl) {
    sources[SOURCES.contours] = {
      type: 'vector',
      tiles: [opts.contourUrl],
      maxzoom: 15,
    };
  }

  if (online) {
    sources[SOURCES.base] = { type: 'vector', url: VECTOR_TILES };

    /* One source per facies, because each is separately toggleable and WMS
       takes the layer name in the request rather than as a filter. */
    for (const a of ALTERATION) {
      sources[alterationSourceId(a.wms)] = {
        type: 'raster',
        tiles: [alterationTileUrl(a.wms)],
        tileSize: 256,
        // The survey is regional. Past this it is being asked a question at a
        // scale it cannot answer, and the polygon edges become a false promise
        // of precision.
        maxzoom: 15,
        attribution: 'Alteration: USGS OFR 2013-1139',
      };
    }
  }

  const layers: Record<string, unknown>[] = [
    /* The ground. Warm stone — the same page colour as the app, so the map
       reads as part of the document rather than a window cut into it. */
    { id: 'ground', type: 'background', paint: { 'background-color': neutral.stone } },
  ];

  if (online) {
    layers.push(
      /* Water. One tone, no outline. In this country it is mostly dry washes,
         which matter as routes, not as water. */
      {
        id: 'water',
        type: 'fill',
        source: SOURCES.base,
        'source-layer': 'water',
        paint: { 'fill-color': '#DCE3DE', 'fill-opacity': 0.75 },
      },
      {
        id: 'waterway',
        type: 'line',
        source: SOURCES.base,
        'source-layer': 'waterway',
        paint: {
          'line-color': '#B9C7BC',
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.6, 16, 2],
        },
      },
    );
  }

  /* Terrain. The primary layer of this map. Warm highlight, cool-neutral
     shadow, exaggerated a little because Arizona relief at claim scale is
     subtle and the whole point is reading the shape of a ridge. */
  layers.push({
    id: 'hillshade',
    type: 'hillshade',
    source: SOURCES.dem,
    paint: {
      'hillshade-exaggeration': 0.55,
      'hillshade-shadow-color': '#6B5F4E',
      'hillshade-highlight-color': neutral.paper,
      'hillshade-accent-color': '#8A7C66',
      // Sun from the northwest, the cartographic convention — relief reads
      // as raised rather than inverted.
      'hillshade-illumination-direction': 315,
    },
  });

  /* Published alteration, above terrain so the polygons sit on the landform,
     but below every layer the team owns — someone else's survey never covers
     your own evidence.

     Saturation is pulled back and opacity is well under half. USGS ships these
     as flat primaries, which at full strength on a warm basemap is unreadable
     in sun and drowns the hillshade the map is actually built around. The hue
     is left alone so a screenshot still cross-references against the published
     figure, and the panel says whose colours they are. */
  if (online) {
    for (const a of ALTERATION) {
      layers.push({
        id: alterationLayerId(a.wms),
        type: 'raster',
        source: alterationSourceId(a.wms),
        layout: { visibility: 'none' },
        paint: {
          'raster-opacity': 0.45,
          'raster-saturation': -0.35,
          'raster-resampling': 'nearest',
        },
      });
    }
  }

  /* Contours, from the DEM. Index lines carry the elevation label. */
  if (opts.contourUrl) layers.push(
    {
      id: 'contour-line',
      type: 'line',
      source: SOURCES.contours,
      'source-layer': 'contours',
      filter: ['!=', ['get', 'level'], 1],
      paint: {
        'line-color': '#9B8B6E',
        'line-opacity': 0.5,
        'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.4, 16, 0.9],
      },
    },
    {
      id: 'contour-index',
      type: 'line',
      source: SOURCES.contours,
      'source-layer': 'contours',
      filter: ['==', ['get', 'level'], 1],
      paint: {
        'line-color': '#8A7855',
        'line-opacity': 0.75,
        'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.8, 16, 1.7],
      },
    },
    {
      id: 'contour-label',
      type: 'symbol',
      source: SOURCES.contours,
      'source-layer': 'contours',
      filter: ['==', ['get', 'level'], 1],
      layout: {
        'symbol-placement': 'line',
        'text-field': ['concat', ['number-format', ['get', 'ele'], {}], ' m'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 10,
        'text-max-angle': 25,
        'symbol-spacing': 320,
      },
      paint: {
        'text-color': '#7A6A48',
        'text-halo-color': neutral.stone,
        'text-halo-width': 1.4,
      },
    },
  );

  if (online) {
    /* Tracks and roads. Thin, warm, unlabelled below the road name — you need
       the two-track you drove in on, not a street network. */
    layers.push(
      {
        id: 'roads-track',
        type: 'line',
        source: SOURCES.base,
        'source-layer': 'transportation',
        filter: ['in', ['get', 'class'], ['literal', ['track', 'path', 'service']]],
        paint: {
          'line-color': '#A89372',
          'line-dasharray': [3, 2],
          'line-width': ['interpolate', ['linear'], ['zoom'], 12, 0.6, 17, 2],
        },
      },
      {
        id: 'roads',
        type: 'line',
        source: SOURCES.base,
        'source-layer': 'transportation',
        // Highways and the paved road in. Minor streets only appear once you
        // are zoomed into a town, which on this property means you are not
        // working. Left at basemap defaults these render as fat white worms
        // that out-shout the terrain, which is the one layer that matters.
        filter: [
          'any',
          ['in', ['get', 'class'], ['literal', ['motorway', 'trunk', 'primary']]],
          ['all', ['>=', ['zoom'], 14], ['in', ['get', 'class'], ['literal', ['secondary', 'tertiary', 'minor']]]],
        ],
        paint: {
          'line-color': '#F3ECDD',
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 14, 1.6, 17, 4],
          'line-opacity': 0.9,
        },
      },
      {
        id: 'place-labels',
        type: 'symbol',
        source: SOURCES.base,
        'source-layer': 'place',
        filter: ['in', ['get', 'class'], ['literal', ['city', 'town', 'village', 'hamlet', 'locality']]],
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['Noto Sans Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 9, 10, 14, 13],
          'text-letter-spacing': 0.08,
          'text-transform': 'uppercase',
        },
        paint: {
          'text-color': neutral.muted,
          'text-halo-color': neutral.stone,
          'text-halo-width': 1.6,
        },
      },
    );
  }

  return {
    version: 8,
    glyphs: GLYPHS,
    sources,
    layers,
    // Terrain is available for a 3D tilt; off by default because a tilted map
    // is worse for reading position, and position is what this is for.
    terrain: undefined,
  };
}

/* ── Overlay paint. Added by the app after the style loads. ─────────────── */

/**
 * Evidence encodes TWO things at once, deliberately: confirmation state by
 * fill, sync state by stroke. Scanning the map you need "is this real yet" and
 * "has this left my phone" to be separable at a glance.
 */
export const evidencePaint = {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 4, 16, 9],
  'circle-color': [
    'case',
    ['==', ['get', 'confirmation'], 'confirmed'],
    win[700],
    ['==', ['get', 'type'], 'structural_feature'],
    neutral.ink,
    brand[600],
  ],
  // A queued record wears a warn ring until it lands — the same semantic as
  // the status pill, so the two never have to be reconciled in someone's head.
  'circle-stroke-color': [
    'case',
    ['==', ['get', 'sync'], 'synced'],
    neutral.paper,
    warn[300],
  ],
  'circle-stroke-width': 2,
};

export const assignmentPaint = {
  fill: { 'fill-color': brand[600], 'fill-opacity': 0.12 },
  /* Dashed brand line: an assignment is an intent, not a fact. Same register
     the assignment card uses on both surfaces. */
  line: {
    'line-color': brand[600],
    'line-width': 2,
    'line-dasharray': [2, 1.5],
  },
};

export const claimPaint = {
  'line-color': neutral.muted,
  'line-width': 1.2,
  'line-opacity': 0.6,
  'line-dasharray': [6, 3],
};

export const positionPaint = {
  dot: {
    'circle-radius': 6,
    'circle-color': neutral.ink,
    'circle-stroke-width': 2.5,
    'circle-stroke-color': neutral.paper,
  },
  /* The accuracy ring is the honest statement about how much to trust the dot,
     so it is drawn to scale in metres rather than as a fixed halo. */
  ring: {
    'circle-color': brand[600],
    'circle-opacity': 0.1,
    'circle-stroke-color': brand[600],
    'circle-stroke-opacity': 0.35,
    'circle-stroke-width': 1,
    'circle-radius': [
      'interpolate',
      ['exponential', 2],
      ['zoom'],
      10,
      ['/', ['get', 'accuracy'], 40],
      20,
      ['/', ['get', 'accuracy'], 0.04],
    ],
  },
};

export const ATTRIBUTION =
  '© OpenStreetMap · OpenFreeMap · Elevation: Mapzen / AWS Terrain Tiles';

/* ── Applying toggle state to a live map ─────────────────────────────────────
 *
 * This was written out identically in MapScreen and DeskMap. It is small, but
 * "small and duplicated" is how the two maps drifted apart the first time, and
 * Addendum A2 makes them matching a requirement rather than a nicety.
 *
 * Typed structurally rather than against maplibre-gl so map-style.ts stays free
 * of the mapping dependency — the capture screen imports this file and should
 * not pull a megabyte of MapLibre with it.
 */
export interface ToggleableMap {
  getLayer(id: string): unknown;
  setLayoutProperty(id: string, name: string, value: unknown): void;
}

export function applyLayerVisibility(
  map: ToggleableMap,
  on: Record<string, boolean>,
): void {
  for (const def of LAYERS) {
    for (const id of def.layers) {
      // A layer can be legitimately absent: contours need a contourUrl, the
      // alteration rasters need `online`. Missing is not an error here.
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', on[def.id] ? 'visible' : 'none');
      }
    }
  }
}

/** Opacity for every alteration raster currently in the style. */
export function applyAlterationOpacity(
  map: ToggleableMap & { setPaintProperty(id: string, name: string, value: unknown): void },
  opacity: number,
): void {
  for (const a of ALTERATION) {
    const id = alterationLayerId(a.wms);
    if (map.getLayer(id)) map.setPaintProperty(id, 'raster-opacity', opacity);
  }
}
